import type { Wallet, WalletDetailsParams } from '@rainbow-me/rainbowkit';
import { createConnector, CreateConnectorFn, injected } from 'wagmi';

type CreateConnector = (walletDetails: {
  rkDetails: any;
}) => CreateConnectorFn;

/**
 * These magic strings are used to identify the Beam provider in the window object.
 * The provider is either found in the `window.ethereum.providers` array or at `window.beam.provider`, if configured correctly.
 */
const BEAM_WINDOW_NAMESPACE = 'beam';
const BEAM_PROVIDER_FLAG = 'isBeam';

function getEIP6963Provider() {
  const _window: any = typeof window !== 'undefined' ? window : undefined;

  if (
    typeof _window === 'undefined' ||
    typeof _window.ethereum === 'undefined'
  ) {
    return;
  }

  const providers = _window.ethereum.providers;
  const flag = BEAM_PROVIDER_FLAG;

  return providers
    ? providers.find((provider: any) => provider[flag])
    : _window.ethereum[flag]
      ? _window.ethereum
      : undefined;
}

function getProvider() {
  return typeof window !== 'undefined'
    ? getEIP6963Provider() ?? (window as any)[BEAM_WINDOW_NAMESPACE]?.provider
    : undefined;
}

function createInjectedConnector(provider?: any): CreateConnector {
  return (walletDetails: WalletDetailsParams) => {
    const injectedConfig = provider
      ? {
          target: () => ({
            id: walletDetails.rkDetails.id,
            name: walletDetails.rkDetails.name,
            provider,
          }),
        }
      : {};

    return createConnector((config) => ({
      ...injected(injectedConfig)(config),
      ...walletDetails,
    }));
  };
}

function getInjectedConnector() {
  const provider = getProvider();
  return createInjectedConnector(provider);
}

export const beamWallet = (): Wallet => ({
  id: 'beam',
  name: 'Beam',
  iconUrl:
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPG1hc2sgaWQ9Im1hc2swXzM0NzhfNTMwNyIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgeD0iMiIgeT0iMiIgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIj4KPGNpcmNsZSBjeD0iMzEuOTk5MyIgY3k9IjMyIiByPSIyOS4zMzMzIiBmaWxsPSIjRDlEOUQ5Ii8+CjwvbWFzaz4KPGcgbWFzaz0idXJsKCNtYXNrMF8zNDc4XzUzMDcpIj4KPHJlY3QgeD0iMi42NjYwMiIgeT0iMi42NjY2MyIgd2lkdGg9IjU4LjY2NjciIGhlaWdodD0iMTQuNjY2NyIgZmlsbD0iIzQwNjZDOSIvPgo8cmVjdCB4PSIyLjY2NjAyIiB5PSIxNy4zMzM0IiB3aWR0aD0iNTguNjY2NyIgaGVpZ2h0PSIxNC42NjY3IiBmaWxsPSIjRUM0NTBEIi8+CjxyZWN0IHg9IjIuNjY2MDIiIHk9IjQ2LjY2NjYiIHdpZHRoPSI1OC42NjY3IiBoZWlnaHQ9IjE0LjY2NjciIGZpbGw9IiMxMEEzNUQiLz4KPHJlY3QgeD0iMi42NjYwMiIgeT0iMzIiIHdpZHRoPSI1OC42NjY3IiBoZWlnaHQ9IjE0LjY2NjciIGZpbGw9IiNGRkE1MEEiLz4KPC9nPgo8L3N2Zz4K',
  rdns: 'com.onbeam',
  iconBackground: '#000000',
  installed: !!getProvider(),
  createConnector: getInjectedConnector(),
});
